import styled, { keyframes } from 'styled-components';

import {
  CASINO_TECH_STATE,
  COLORS,
  BRAND_DEPLOYMENT_ANIM,
  BRAND_DEPLOYMENT_CARD_LIFT,
  BRAND_DEPLOYMENT_CONFIRM_BORDER,
  BRAND_DEPLOYMENT_GLASS,
  BRAND_DEPLOYMENT_SURFACE,
  REDUCED_MOTION,
} from '../../../../../theme/tokens';
import { Text } from '../../../../../components';
import { CardShell } from '../cardBase.styles';

// Mask-composite alpha idiom that cuts the gradient down to a 1px ring; the `#000` is
// a structural alpha source, not a colour.
const CONFIRM_RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)'; // design-allow

/**
 * Success card: composes the shared fan-positioning shell over the section's own base
 * colour (near-black on dark, white on light), with its OWN aurora rendered inside (the
 * live motion that the section's aurora hands off once the deploy completes). A
 * translucent tint over the aurora keeps the success content legible; the content sits
 * on top; a magenta -> cyan gradient rim frames it.
 *
 * On dark the card separates from the section by fill (it is frostier than the base). On
 * light both are white, so the separation comes from the lift instead (no-op on dark).
 */
export const Root = styled(CardShell)`
  background: ${BRAND_DEPLOYMENT_SURFACE};
  box-shadow: ${BRAND_DEPLOYMENT_CARD_LIFT};

  /* Magenta -> cyan gradient border (a masked ring hugging the rounded edge). */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 3;
    border-radius: inherit;
    padding: 1px;
    opacity: 0.32;
    background: ${BRAND_DEPLOYMENT_CONFIRM_BORDER};
    -webkit-mask: ${CONFIRM_RING_MASK};
    mask: ${CONFIRM_RING_MASK};
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

/** Translucent tint over the card aurora (z1) so the success content stays legible. */
export const Scrim = styled.span`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: ${BRAND_DEPLOYMENT_GLASS};
`;

/** The success content, above the card aurora (z0) + the scrim (z1). */
export const Content = styled.div`
  position: relative;
  z-index: 2;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 16px 12px 14px;
`;

/** Check + headline, centred in the space above the CTA (equally distributed between
 *  the card top and the button). */
export const Hero = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

/** The check pops in (scale overshoot + fade) once the card has settled. */
const checkPop = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  55% {
    transform: scale(1.28);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

/** Soft success halo around the check glyph (tinted from the live-deploy accent). */
export const Glyph = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-top: 2px;
  background: color-mix(in srgb, ${CASINO_TECH_STATE.live.accent} 18%, transparent);
  animation: ${checkPop} ${BRAND_DEPLOYMENT_ANIM.CHECK_MS}ms ${BRAND_DEPLOYMENT_ANIM.CHECK_DELAY_MS}ms cubic-bezier(0.22, 1, 0.36, 1)
    both;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const Headline = styled(Text)`
  font-size: 17px;
  font-weight: 600;
  line-height: 1.25;
  color: ${COLORS.heading};
`;

/** Vertical CTA stack: the primary fills the row; "View details" sits under it. */
export const Actions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`;

/**
 * The primary "Visit now" CTA — a full-width pill matching the shared Button's
 * primary look (same tokens/height), but with a right-hand arrow that nudges right
 * on hover. (The shared Button can't right-place + animate an icon, hence local.)
 */
export const VisitButton = styled.button`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  border: 1px solid ${COLORS.ctaSolid};
  border-radius: 99px;
  background: ${COLORS.ctaSolid};
  color: ${COLORS.ctaSolidText};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  /* The arrow slides right a touch on hover (smooth), and rests otherwise. */
  & .visit-arrow {
    display: inline-flex;
    transition: transform ${BRAND_DEPLOYMENT_ANIM.LINK_MS}ms ease;
  }
  &:hover:not(:disabled) .visit-arrow {
    transform: translateX(4px);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolidText};
    outline-offset: 2px;
  }

  ${REDUCED_MOTION} {
    & .visit-arrow {
      transition: none;
    }
  }
`;

/** Plain-text secondary affordance ("View details") opening the summary modal. */
export const DetailsLink = styled.button`
  align-self: center;
  padding: 4px 6px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition: color ${BRAND_DEPLOYMENT_ANIM.LINK_MS}ms ease;

  &:hover {
    color: ${COLORS.text};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${CASINO_TECH_STATE.live.accent};
    outline-offset: 2px;
    border-radius: 6px;
  }
`;
